import http, { type ApiRequestConfig } from '@services/httpService'
import { downloadFile } from '@lib/helperFile'
import type {
  CreateDownloadRequestInput,
  DownloadRequestDto,
  DownloadRequestFilter,
  DownloadRequestPreviewDto,
  MyDownloadSummaryDto,
} from '@models/downloadCenter/downloadRequest'

interface PagedResult<T> {
  totalCount: number
  items: T[]
}

/**
 * Client của Download Request Center.
 *
 * Lưu ý một chỗ KHÔNG theo khuôn ABP dynamic API: `download()` gọi
 * `api/DownloadRequest/Download` (controller riêng) chứ không phải
 * `api/services/app/DownloadRequest/...`. Dynamic API bọc kết quả vào envelope và `byte[]` sẽ bị
 * base64 hóa — không dùng làm blob được.
 */
class DownloadRequestService {
  /** Đếm trước số item để biết có phải hiện popup xác nhận hay không. */
  public async getPreview(
    input: CreateDownloadRequestInput
  ): Promise<DownloadRequestPreviewDto> {
    const result = await http.post(
      'api/services/app/DownloadRequest/GetPreview',
      input
    )
    return result.data.result
  }

  public async createRequest(
    input: CreateDownloadRequestInput
  ): Promise<DownloadRequestDto> {
    const result = await http.post(
      'api/services/app/DownloadRequest/CreateRequest',
      input
    )
    return result.data.result
  }

  public async getAll(
    params: DownloadRequestFilter
  ): Promise<PagedResult<DownloadRequestDto>> {
    const result = await http.get('api/services/app/DownloadRequest/GetAll', {
      params,
    })
    return result.data.result
  }

  public async get(id: number): Promise<DownloadRequestDto> {
    const result = await http.get('api/services/app/DownloadRequest/Get', {
      params: { id },
    })
    return result.data.result
  }

  /**
   * Số cho badge ở header. Chạy nền theo nhịp polling nên tắt progress bar — nếu không, cứ mỗi chu kỳ
   * thanh loading lại nháy một lần dù người dùng không thao tác gì.
   */
  public async getMySummary(signal?: AbortSignal): Promise<MyDownloadSummaryDto> {
    const config: ApiRequestConfig = {
      signal,
      skipProgressBar: true,
      timeout: 10000,
    }
    const result = await http.get(
      'api/services/app/DownloadRequest/GetMySummary',
      config
    )
    return result.data.result
  }

  public async cancel(id: number): Promise<DownloadRequestDto> {
    const result = await http.post(
      'api/services/app/DownloadRequest/Cancel',
      { id }
    )
    return result.data.result
  }

  public async retry(id: number): Promise<DownloadRequestDto> {
    const result = await http.post('api/services/app/DownloadRequest/Retry', {
      id,
    })
    return result.data.result
  }

  public async delete(id: number): Promise<void> {
    await http.delete('api/services/app/DownloadRequest/Delete', {
      params: { id },
    })
  }

  /**
   * Tải tệp kết quả qua XHR blob (đi qua interceptor nên có Bearer). Cố ý KHÔNG dùng `window.open`
   * với token trên query string — link đó copy được và rò ra ngoài.
   */
  public async download(id: number, fileName?: string): Promise<void> {
    const res = await http.get('api/DownloadRequest/Download', {
      params: { id },
      responseType: 'blob',
    })
    downloadFile(res.data, fileName || `download-${id}.xlsx`)
  }
}

export default new DownloadRequestService()
